import moment from "moment";
import {
  calculateAndFormatNoAbs,
  findCust,
  findFundAbbById,
  findStrategy,
  hashInvestment,
} from "../../component/Methods";

export const formatSigned = (value, formatter = calculateAndFormatNoAbs) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  const formatted = formatter(Math.abs(n));
  // calculateAndFormatNoAbs returns the raw value when falsy (e.g. 0) — normalize
  const text =
    formatted === 0 || formatted === "0"
      ? "0"
      : formatted == null || formatted === ""
        ? String(Math.abs(n))
        : String(formatted);
  return n < 0 ? `(${text})` : text;
};

export const formatDate = (date) =>
  date ? moment(date).format("MMM DD, YYYY") : "-";

const sameDayKey = (date) =>
  date == null ? "" : moment(date).format("YYYY-MM-DD");

const uniqueOrMultiple = (values, resolveOne) => {
  const unique = [...new Set(values.filter(Boolean))];
  if (unique.length === 1) return resolveOne(unique[0]);
  if (unique.length > 1) return "Multiple";
  return "-";
};

export const getBoxedPositionInvestment = (item) =>
  Number(item?.investment ?? item?.lots?.[0]?.ticker?.investment);

/** Aggregate parent display fields from child lots. */
export const buildParentDisplay = (position, strategies, custodians, funds) => {
  const lots = Array.isArray(position?.lots) ? position.lots : [];
  const strategyIds = [...new Set(lots.map((l) => l?.strategy).filter(Boolean))];
  const custodianIds = [
    ...new Set(
      lots
        .map((l) => l?.account?.custodian || position?.custodian)
        .filter(Boolean)
    ),
  ];
  const dateKeys = [
    ...new Set(lots.map((l) => sameDayKey(l?.date)).filter(Boolean)),
  ];
  const totalCost = lots.reduce((sum, l) => sum + (Number(l?.cost) || 0), 0);
  const totalPrice = lots.reduce((sum, l) => {
    const price = Number(l?.sharePrice);
    return Number.isFinite(price) ? sum + price : sum;
  }, 0);

  const positionTypes = lots.map((l) =>
    Number(l?.position) === 0 ? "Long" : "Short"
  );

  let dateLabel = "-";
  if (dateKeys.length === 1) {
    const lot = lots.find((l) => sameDayKey(l?.date) === dateKeys[0]);
    dateLabel = formatDate(lot?.date);
  } else if (dateKeys.length > 1) {
    dateLabel = "Multiple";
  }

  let investmentLabel = uniqueOrMultiple(
    lots.map((l) => l?.ticker?.investment).filter((v) => v != null),
    (id) => hashInvestment(Number(id)) || "-"
  );
  if (investmentLabel === "-") {
    investmentLabel = hashInvestment(Number(position?.investment)) || "-";
  }

  return {
    dateLabel,
    positionType: uniqueOrMultiple(positionTypes, (v) => v),
    tickerLabel: position?.tickerSymbol
      ? String(position.tickerSymbol).toUpperCase()
      : "-",
    investmentLabel,
    fundLabel: position?.fund
      ? findFundAbbById(position.fund, funds) || "-"
      : "-",
    strategyLabel: uniqueOrMultiple(
      strategyIds,
      (id) => findStrategy(id, strategies) || "-"
    ),
    custodianLabel: uniqueOrMultiple(
      custodianIds,
      (id) => findCust(id, custodians) || "-"
    ),
    shares: position?.totalShares,
    cost: totalCost,
    price: lots.some((l) => Number.isFinite(Number(l?.sharePrice)))
      ? totalPrice
      : null,
  };
};

export const buildStableRowId = (position) =>
  `${position.tickerSymbol || ""}-${position.fund || ""}-${position.custodian || ""}`;

export const sortBoxedPositions = ({
  positions,
  sortField,
  sortDirection,
  funds,
  custodians,
  strategies,
}) => {
  return [...positions].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === "tickerSymbol") {
      const aStartsWithNumber = /^\d/.test((aValue || "").toLowerCase());
      const bStartsWithNumber = /^\d/.test((bValue || "").toLowerCase());
      if (aStartsWithNumber && !bStartsWithNumber) return 1;
      if (!aStartsWithNumber && bStartsWithNumber) return -1;
      aValue = (aValue || "").toLowerCase();
      bValue = (bValue || "").toLowerCase();
    } else if (sortField === "totalShares" || sortField === "cost") {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else if (sortField === "fund") {
      aValue = (findFundAbbById(aValue, funds) || "").toLowerCase();
      bValue = (findFundAbbById(bValue, funds) || "").toLowerCase();
    } else if (sortField === "custodian") {
      aValue = (findCust(aValue, custodians) || "").toLowerCase();
      bValue = (findCust(bValue, custodians) || "").toLowerCase();
    } else if (sortField === "date") {
      aValue = a?.lots?.[0]?.date || 0;
      bValue = b?.lots?.[0]?.date || 0;
    } else if (sortField === "strategy") {
      aValue = (
        findStrategy(a?.lots?.[0]?.strategy, strategies) || ""
      ).toLowerCase();
      bValue = (
        findStrategy(b?.lots?.[0]?.strategy, strategies) || ""
      ).toLowerCase();
    } else if (sortField === "investmentType") {
      aValue = (
        hashInvestment(getBoxedPositionInvestment(a)) || ""
      ).toLowerCase();
      bValue = (
        hashInvestment(getBoxedPositionInvestment(b)) || ""
      ).toLowerCase();
    } else {
      aValue = (aValue || "").toLowerCase();
      bValue = (bValue || "").toLowerCase();
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });
};

export const filterAndPrepareBoxedPositions = ({
  originalPositions,
  search,
  sortField,
  sortDirection,
  funds,
  custodians,
  strategies,
}) => {
  let filtered = originalPositions || [];

  if (search) {
    const searchLower = search.toLowerCase();
    filtered = filtered.filter((item) => {
      const tickerMatch = (item.tickerSymbol || "")
        .toLowerCase()
        .includes(searchLower);
      const investmentMatch = (
        hashInvestment(getBoxedPositionInvestment(item)) || ""
      )
        .toLowerCase()
        .includes(searchLower);
      const fundMatch = findFundAbbById(item.fund, funds)
        ?.toLowerCase()
        .includes(searchLower);
      const custodianMatch = findCust(item.custodian, custodians)
        ?.toLowerCase()
        .includes(searchLower);
      const strategyMatch = (item.lots || []).some((lot) =>
        findStrategy(lot?.strategy, strategies)
          ?.toLowerCase()
          .includes(searchLower)
      );
      const totalSharesMatch =
        item.totalShares !== undefined &&
        item.totalShares !== null &&
        String(item.totalShares).includes(search);
      return (
        tickerMatch ||
        investmentMatch ||
        fundMatch ||
        custodianMatch ||
        strategyMatch ||
        totalSharesMatch
      );
    });
  }

  const sorted = sortBoxedPositions({
    positions: filtered,
    sortField,
    sortDirection,
    funds,
    custodians,
    strategies,
  });

  return sorted.map((position) => {
    const totalCost = (position.lots || []).reduce(
      (sum, l) => sum + (Number(l?.cost) || 0),
      0
    );
    return {
      id: buildStableRowId(position),
      ...position,
      cost: totalCost,
    };
  });
};
